import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { APP_VERSION } from "@/lib/version";
import { Loader2, Eye, EyeOff, ArrowRight } from "@/components/icons";

// LOTE V2-C — tela de entrada: computador (versão C) + celular (versão A).
// Barlow carregada só nesta tela; nada mais do app muda.
const LOGO_SRC = "/marca/mpires-connect-cor-petroleo.png";
const LOGO_FALLBACK = "/mpires-logo-trim.png";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 600);

  useEffect(() => {
    // Barlow do Google Fonts, só nesta rota.
    const id = "login-barlow-font";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;800&display=swap";
      document.head.appendChild(link);
    }
    const onResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (user) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success("Bem-vindo de volta!");
      if (u?.must_change_password) navigate("/trocar-senha", { replace: true });
      else navigate(from, { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "E-mail ou senha incorretos"));
    } finally {
      setLoading(false);
    }
  };

  const onLogoError = (e) => { if (e.currentTarget.src.indexOf("mpires-logo") === -1) e.currentTarget.src = LOGO_FALLBACK; };

  // Estilos compartilhados do formulário (fundo escuro, branco 8% / borda branca 28%).
  const inputCls = "w-full px-4 rounded-xl text-white outline-none transition-colors";
  const inputStyle = { background: "rgba(255,255,255,0.08)", border: "1.5px solid rgba(255,255,255,0.28)", fontFamily: "'Barlow', sans-serif", fontWeight: 500 };
  const labelStyle = { color: "#D7E6EE", fontFamily: "'Barlow', sans-serif", fontWeight: 600, letterSpacing: 0.1 };

  // ===== COMPUTADOR (>= 601px) =====
  if (!isMobile) {
    return (
      <div data-testid="login-form" style={{ fontFamily: "'Barlow', sans-serif", background: "#003E62", color: "#fff", minHeight: "100vh", width: "100%", position: "relative", overflow: "hidden" }}>
        {/* V2-C.1 — Grupo de círculos com MESMO centro: right 220px, top 62%. z-index 1. */}
        <div aria-hidden style={{ position: "absolute", width: 1500, height: 1500, borderRadius: "50%", background: "#004D74", right: -530, top: "calc(62% - 750px)", pointerEvents: "none", zIndex: 1 }} />
        <div aria-hidden style={{ position: "absolute", width: 1000, height: 1000, borderRadius: "50%", background: "#006588", right: -280, top: "calc(62% - 500px)", pointerEvents: "none", zIndex: 1 }} />
        <div aria-hidden style={{ position: "absolute", width: 360, height: 360, borderRadius: "50%", background: "#008BB0", right: 40, top: "calc(62% - 180px)", pointerEvents: "none", zIndex: 1 }} />
        {/* Linha diagonal 9° — passa pelo centro do círculo branco. z-index 2 (acima dos ciano, abaixo do branco). */}
        <div aria-hidden style={{ position: "absolute", width: "1.5px", height: "140%", background: "rgba(255,255,255,0.85)", right: 220, top: "calc(62% - 70%)", transform: "rotate(9deg)", transformOrigin: "center", pointerEvents: "none", zIndex: 2 }} />
        {/* Círculo branco 290 px com o logo DENTRO (sem caixa). z-index 3 (cobre a linha). */}
        <div aria-hidden style={{ position: "absolute", width: 290, height: 290, borderRadius: "50%", background: "#fff", right: 75, top: "calc(62% - 145px)", boxShadow: "0 20px 60px rgba(0,20,35,0.4)", pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3 }}>
          <img src={LOGO_SRC} onError={onLogoError} alt="MPires Connect" style={{ width: 186, height: "auto", display: "block", background: "transparent" }} />
        </div>

        <form onSubmit={submit} style={{ position: "relative", zIndex: 4, padding: "224px 0 0 72px", maxWidth: 960 }}>
          <h1 style={{ fontFamily: "'Barlow', sans-serif", fontWeight: 800, fontSize: 64, lineHeight: 1.05, letterSpacing: -0.5, margin: 0, width: 760 }}>
            <span style={{ color: "#fff", display: "block", whiteSpace: "nowrap" }}>Inteligência comercial</span>
            <span style={{ color: "#99D1DF", display: "block", whiteSpace: "nowrap" }}>na palma da sua mão.</span>
          </h1>
          <p style={{ color: "#D7E6EE", fontSize: 18, fontWeight: 400, marginTop: 20, marginBottom: 48, maxWidth: 520, lineHeight: 1.45 }}>
            Funil, previsão, prospecção e faturamento da sua carteira, num só lugar.
          </p>

          <div style={{ maxWidth: 400 }}>
            <label style={{ ...labelStyle, fontSize: 16, display: "block", marginBottom: 8 }} htmlFor="login-email-input">E-mail corporativo</label>
            <input id="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="nome@mpires.com.br" className={inputCls} style={{ ...inputStyle, height: 54, fontSize: 18 }} autoComplete="email" />

            <label style={{ ...labelStyle, fontSize: 16, display: "block", marginTop: 18, marginBottom: 8 }} htmlFor="login-password-input">Senha</label>
            <div style={{ position: "relative" }}>
              <input id="login-password-input" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" className={inputCls} style={{ ...inputStyle, height: 54, fontSize: 18, paddingRight: 48 }} autoComplete="current-password" />
              <button type="button" onClick={() => setShow((s) => !s)} data-testid="login-eye" aria-label={show ? "Esconder senha" : "Mostrar senha"} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "transparent", border: 0, color: "rgba(255,255,255,0.72)", cursor: "pointer", padding: 4 }}>
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <Link to="/forgot-password" style={{ color: "#99D1DF", fontFamily: "'Barlow', sans-serif", fontWeight: 600, fontSize: 15, textDecoration: "none" }}>Esqueci minha senha</Link>
            </div>

            <button type="submit" disabled={loading} data-testid="login-submit" style={{ width: "100%", height: 58, marginTop: 22, borderRadius: 12, border: 0, background: "#fff", color: "#003E62", fontFamily: "'Barlow', sans-serif", fontWeight: 800, fontSize: 19, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: loading ? "default" : "pointer", opacity: loading ? 0.75 : 1 }}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Entrar <ArrowRight className="w-5 h-5" /></>)}
            </button>

            <p style={{ color: "#B9CDD9", fontSize: 15, marginTop: 18, fontWeight: 400 }} data-testid="login-no-signup">Não tem acesso? Fale com o administrador.</p>
          </div>
        </form>

        <div style={{ position: "absolute", left: 72, right: 72, bottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center", color: "#B9CDD9", fontFamily: "'Barlow', sans-serif", fontWeight: 600, fontSize: 12, letterSpacing: 0.6, zIndex: 5, pointerEvents: "none" }}>
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <img src="/marca/allia-horizontal-branco.png" alt="Allia" style={{ height: 22, width: "auto", display: "block" }} />
            <span aria-hidden style={{ display: "inline-block", width: 1, height: 16, background: "rgba(255,255,255,0.45)", margin: "0 10px" }} />
            <span>EMPRESA ASSOCIADA</span>
          </span>
          <span style={{ letterSpacing: 0.3, fontWeight: 400, textShadow: "0 1px 2px rgba(0,20,35,0.4)" }}>© MPires · {APP_VERSION}</span>
        </div>
      </div>
    );
  }

  // ===== CELULAR (<= 600px, versão A) =====
  return (
    <div data-testid="login-form" style={{ fontFamily: "'Barlow', sans-serif", background: "#16385B", color: "#fff", minHeight: "100vh", width: "100%", position: "relative", overflow: "hidden", padding: "0 24px" }}>
      {/* Círculos saindo do canto superior esquerdo — descidos pela área segura do aparelho (V2-C.4). */}
      <div aria-hidden style={{ position: "absolute", width: 620, height: 620, borderRadius: "50%", background: "#105176", left: -340, top: "calc(env(safe-area-inset-top, 0px) - 344px)", pointerEvents: "none" }} />
      <div aria-hidden style={{ position: "absolute", width: 420, height: 420, borderRadius: "50%", background: "#086A91", left: -240, top: "calc(env(safe-area-inset-top, 0px) - 244px)", pointerEvents: "none" }} />
      <div aria-hidden style={{ position: "absolute", width: 180, height: 180, borderRadius: "50%", background: "#008BB0", left: -70, top: "calc(env(safe-area-inset-top, 0px) - 74px)", pointerEvents: "none" }} />
      <div aria-hidden style={{ position: "absolute", width: 140, height: 140, borderRadius: "50%", background: "#fff", left: 24, top: "calc(env(safe-area-inset-top, 0px) + 24px)", boxShadow: "0 12px 32px rgba(0,20,35,0.35)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
        <img src={LOGO_SRC} onError={onLogoError} alt="MPires Connect" style={{ width: 92, height: "auto", display: "block" }} />
      </div>

      <form onSubmit={submit} style={{ position: "relative", zIndex: 2, paddingTop: "calc(196px + env(safe-area-inset-top, 0px))" }}>
        <h1 style={{ fontFamily: "'Barlow', sans-serif", fontWeight: 800, fontSize: 32, lineHeight: 1.08, margin: 0 }}>
          <span style={{ color: "#fff", display: "block" }}>Inteligência comercial</span>
          <span style={{ color: "#99D1DF", display: "block" }}>na palma da sua mão.</span>
        </h1>
        <p style={{ color: "#D7E6EE", fontSize: 15, fontWeight: 400, marginTop: 14, marginBottom: 28, lineHeight: 1.45 }}>
          Funil, previsão, prospecção e faturamento da sua carteira, num só lugar.
        </p>

        <label style={{ ...labelStyle, fontSize: 15, display: "block", marginBottom: 8 }} htmlFor="login-email-input-m">E-mail corporativo</label>
        <input id="login-email-input-m" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="nome@mpires.com.br" className={inputCls} style={{ ...inputStyle, height: 50, fontSize: 17 }} autoComplete="email" />

        <label style={{ ...labelStyle, fontSize: 15, display: "block", marginTop: 18, marginBottom: 8 }} htmlFor="login-password-input-m">Senha</label>
        <div style={{ position: "relative" }}>
          <input id="login-password-input-m" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" className={inputCls} style={{ ...inputStyle, height: 50, fontSize: 17, paddingRight: 48 }} autoComplete="current-password" />
          <button type="button" onClick={() => setShow((s) => !s)} data-testid="login-eye" aria-label={show ? "Esconder senha" : "Mostrar senha"} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "transparent", border: 0, color: "rgba(255,255,255,0.72)", cursor: "pointer", padding: 4 }}>
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <Link to="/forgot-password" style={{ color: "#99D1DF", fontFamily: "'Barlow', sans-serif", fontWeight: 600, fontSize: 15, textDecoration: "none" }}>Esqueci minha senha</Link>
        </div>

        <button type="submit" disabled={loading} data-testid="login-submit" style={{ width: "100%", height: 52, marginTop: 20, borderRadius: 12, border: 0, background: "#fff", color: "#003E62", fontFamily: "'Barlow', sans-serif", fontWeight: 800, fontSize: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: loading ? "default" : "pointer", opacity: loading ? 0.75 : 1 }}>
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Entrar <ArrowRight className="w-5 h-5" /></>)}
        </button>

        <p style={{ color: "#B9CDD9", fontSize: 13, marginTop: 16, fontWeight: 400 }} data-testid="login-no-signup">Sem acesso? Fale com o admin.</p>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#B9CDD9", fontFamily: "'Barlow', sans-serif", fontWeight: 600, fontSize: 11, letterSpacing: 0.5, marginTop: 36, paddingBottom: 24 }}>
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <img src="/marca/allia-horizontal-branco.png" alt="Allia" style={{ height: 18, width: "auto", display: "block" }} />
            <span aria-hidden style={{ display: "inline-block", width: 1, height: 16, background: "rgba(255,255,255,0.45)", margin: "0 10px" }} />
            <span>EMPRESA ASSOCIADA</span>
          </span>
          <span style={{ letterSpacing: 0.3, fontWeight: 400 }}>© MPires · {APP_VERSION}</span>
        </div>
      </form>
    </div>
  );
}
